/**
 * /akun/edit-profil — Edit Child Profile (Phase 5.3).
 *
 * Per doc 08 §4:
 *  - Edit nama (1-20 char, same as onboarding)
 *  - Edit usia (slider 2-6, with PG/TK A/TK B labels)
 *  - Edit avatar (4x3 grid of 12 benda sehari-hari — see AVATAR_LIST below)
 *  - "Simpan" button calls PUT /v1/child/:id + invalidates the local cache
 *  - On success, navigate back to /akun/parent-zone
 *
 * Avatar list is hardcoded Y1 (no server source). The avatar_id column
 * already exists in child_profile (0-99); we just pick a curated set.
 *
 * Y1 reuses the OnboardingStepShell layout idea (header + content + CTA)
 * but as a self-contained Edit screen (no parent state machine).
 */

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { AppIcon } from '@/components/app-icon';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { avatarIcon } from '@/lib/icons';
import { getActiveChild, updateChild } from '@/lib/api/children';
import { loadSession } from '@/lib/parent-session';
import { isGuestSession } from '@/lib/auth';
import { ageToClassId, classIdToLabel } from '@/lib/age-class';
import { ApiError } from '@/lib/api/client';

import type { ChildProfile } from '@funpaper/shared';

// 12 avatars per doc 08 §15. Ikon clay generated (lib/icons.ts) memakai
// benda sehari-hari dengan maksimal tiga warna utama.
const AVATAR_LIST: { id: number; label: string }[] = [
  { id: 1, label: 'Tas sekolah' },
  { id: 2, label: 'Buku' },
  { id: 3, label: 'Pensil' },
  { id: 4, label: 'Kotak makan' },
  { id: 5, label: 'Botol minum' },
  { id: 6, label: 'Payung' },
  { id: 7, label: 'Jam' },
  { id: 8, label: 'Lampu meja' },
  { id: 9, label: 'Sepatu' },
  { id: 10, label: 'Gunting' },
  { id: 11, label: 'Kotak pensil' },
  { id: 12, label: 'Kotak tisu' },
];

const NAME_MAX = 20;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; child: ChildProfile }
  | { kind: 'error'; message: string };

export default function EditProfilScreen() {
  const router = useRouter();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [name, setName] = useState('');
  const [age, setAge] = useState(4);
  const [avatarId, setAvatarId] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // --------------------------------------------------------------------------
  // Load
  // --------------------------------------------------------------------------

  useEffect(() => {
    (async () => {
      // Session check.
      const session = await loadSession();
      if (!session && !isGuestSession()) {
        router.replace('/akun/pin' as never);
        return;
      }
      try {
        const child = await getActiveChild();
        if (!child) {
          setState({ kind: 'error', message: 'Belum ada profil anak.' });
          return;
        }
        setState({ kind: 'ready', child });
        setName(child.name);
        setAge(child.age);
        setAvatarId(child.avatarId);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Gagal memuat profil.';
        setState({ kind: 'error', message });
      }
    })();
  }, [router]);

  // --------------------------------------------------------------------------
  // Save
  // --------------------------------------------------------------------------

  const onSave = useCallback(async () => {
    if (state.kind !== 'ready') return;
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setError('Nama tidak boleh kosong.');
      return;
    }
    if (trimmedName.length > NAME_MAX) {
      setError(`Nama maksimal ${NAME_MAX} karakter.`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const classId = ageToClassId(age as 2 | 3 | 4 | 5 | 6);
      await updateChild(state.child.id, {
        name: trimmedName,
        age,
        classId,
        avatarId,
      });
      setToast('✓ Profil disimpan');
      setTimeout(() => router.back(), 600);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Gagal menyimpan profil.';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [state, name, age, avatarId, router]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (state.kind === 'loading') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.kind === 'error') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ThemedText type="subtitle" style={styles.errorTitle}>{state.message}</ThemedText>
            <Pressable onPress={() => router.back()} style={styles.retryBtn}>
              <Text style={styles.retryText}>Kembali</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <Text style={styles.backArrow}>←</Text>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Edit Profil Anak</ThemedText>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          {/* Name */}
          <View style={styles.field}>
            <ThemedText type="smallBold" style={styles.label}>Nama Anak</ThemedText>
            <TextInput
              value={name}
              onChangeText={(v) => setName(v.slice(0, NAME_MAX))}
              placeholder="Contoh: Aisyah"
              placeholderTextColor="#9CA3AF"
              style={styles.input}
              maxLength={NAME_MAX}
              accessibilityLabel={`Nama anak, maksimal ${NAME_MAX} karakter`}
            />
            <ThemedText type="small" themeColor="textSecondary" style={styles.charCount}>
              {name.length}/{NAME_MAX}
            </ThemedText>
          </View>

          {/* Age */}
          <View style={styles.field}>
            <ThemedText type="smallBold" style={styles.label}>
              Usia: {age} tahun ({ageLabel(age)})
            </ThemedText>
            <View style={styles.ageRow}>
              {([2, 3, 4, 5, 6] as const).map((a) => (
                <Pressable
                  key={a}
                  onPress={() => setAge(a)}
                  style={({ pressed }) => [
                    styles.ageChip,
                    age === a && styles.ageChipActive,
                    pressed && styles.btnPressed,
                  ]}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: age === a }}
                  accessibilityLabel={`Usia ${a} tahun (${ageLabel(a)})`}
                >
                  <Text
                    style={[styles.ageChipText, age === a && styles.ageChipTextActive]}
                  >
                    {a}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>

          {/* Avatar */}
          <View style={styles.field}>
            <ThemedText type="smallBold" style={styles.label}>Avatar</ThemedText>
            <View style={styles.avatarGrid}>
              {AVATAR_LIST.map((av) => {
                const active = av.id === avatarId;
                return (
                  <Pressable
                    key={av.id}
                    onPress={() => setAvatarId(av.id)}
                    style={({ pressed }) => [
                      styles.avatarCell,
                      active && styles.avatarCellActive,
                      pressed && styles.btnPressed,
                    ]}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={`Avatar ${av.label}`}
                  >
                    <AppIcon source={avatarIcon(av.id)} size={40} />
                    {active ? (
                      <View style={styles.avatarCheck}>
                        <Text style={styles.avatarCheckText}>✓</Text>
                      </View>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          </View>

          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          {toast ? <Text style={styles.toastText}>{toast}</Text> : null}
        </ScrollView>

        <View style={styles.bottomActions}>
          <Pressable
            onPress={onSave}
            disabled={saving}
            style={({ pressed }) => [styles.saveBtn, saving && styles.btnDisabled, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Simpan perubahan"
          >
            <Text style={styles.saveBtnText}>
              {saving ? 'Menyimpan…' : 'Simpan'}
            </Text>
          </Pressable>
        </View>
      </SafeAreaView>
    </ThemedView>
  );
}

function ageLabel(age: number): string {
  return classIdToLabel(ageToClassId(age as 2 | 3 | 4 | 5 | 6));
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: 18, fontWeight: '700', color: '#1A0B5C' },
  scrollContent: { padding: Spacing.four, gap: Spacing.four, paddingBottom: Spacing.five },
  field: { gap: Spacing.two },
  label: { color: '#1A0B5C', fontSize: 15 },
  input: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    fontSize: 16,
    color: '#1A0B5C',
  },
  charCount: { textAlign: 'right', fontSize: 11 },
  ageRow: { flexDirection: 'row', gap: Spacing.two, flexWrap: 'wrap' },
  ageChip: {
    width: 56, height: 56,
    borderRadius: Radii.medium,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    alignItems: 'center', justifyContent: 'center',
  },
  ageChipActive: { backgroundColor: Brand.primary, borderColor: Brand.primary },
  ageChipText: { fontSize: 18, fontWeight: '600', color: '#1A0B5C' },
  ageChipTextActive: { color: '#ffffff' },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.two,
  },
  avatarCell: {
    width: 64, height: 64,
    borderRadius: Radii.medium,
    backgroundColor: '#ffffff',
    borderWidth: 2,
    borderColor: '#e5e5e5',
    alignItems: 'center', justifyContent: 'center',
  },
  avatarCellActive: { borderColor: Brand.primary, backgroundColor: 'rgba(86, 63, 122, 0.08)' },
  avatarCheck: {
    position: 'absolute',
    top: -6, right: -6,
    width: 22, height: 22,
    borderRadius: 11,
    backgroundColor: Brand.primary,
    alignItems: 'center', justifyContent: 'center',
  },
  avatarCheckText: { color: '#ffffff', fontSize: 12, fontWeight: '700' },
  errorText: { color: '#e53e3e', fontSize: 13, textAlign: 'center' },
  toastText: { color: Brand.primary, fontSize: 13, textAlign: 'center' },
  bottomActions: { padding: Spacing.four, paddingBottom: Spacing.four },
  saveBtn: {
    backgroundColor: Brand.primary,
    paddingVertical: 14,
    borderRadius: Radii.medium,
    alignItems: 'center',
  },
  saveBtnText: { color: '#ffffff', fontSize: 16, fontWeight: '700' },
  btnPressed: { opacity: 0.7 },
  btnDisabled: { opacity: 0.5 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: Spacing.four, gap: Spacing.three },
  errorTitle: { color: Brand.primary, textAlign: 'center' },
  retryBtn: { marginTop: Spacing.two, paddingHorizontal: Spacing.four, paddingVertical: Spacing.two, backgroundColor: Brand.primary, borderRadius: Radii.medium },
  retryText: { color: '#ffffff', fontSize: 14, fontWeight: '600' },
});
